import { useEffect, useRef, useState } from 'react'
import './challenge.css'

/**
 * The "I am human" check in front of a write anybody can reach.
 *
 * One component for every public form, so the contact page and joining ask the
 * same way and a change to how the challenge is drawn is one edit. What it
 * yields is a token the server verifies — this page decides nothing about
 * whether a write is allowed, and a page that rendered no widget still submits
 * and is refused or allowed by the server.
 *
 * `siteKey` null means the install has none configured, and the widget is not
 * drawn at all: an empty bordered box with no explanation is worse than the
 * absence of one, and the server already knows what to do about it.
 */

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/** What the vendor's script puts on `window` once it has loaded. */
type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string
      theme: 'auto'
      size: 'flexible'
      callback: (token: string) => void
      'expired-callback': () => void
      'error-callback': () => void
    },
  ) => string
  /** Clear a solved widget and offer the check again. */
  reset: (id: string) => void
  remove: (id: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

/**
 * Load the vendor script once per document.
 *
 * A promise held at module scope rather than a tag appended per mount: two
 * forms on one page, or a form that mounts twice, would otherwise each add a
 * script and race to define the same global.
 */
let loading: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (loading) return loading
  loading = new Promise<void>((resolve, reject) => {
    const tag = document.createElement('script')
    tag.src = SCRIPT_SRC
    tag.async = true
    tag.defer = true
    tag.onload = () => resolve()
    tag.onerror = () => {
      /* Cleared so a later attempt can try again — a network blip must not
         leave every form on the site permanently unable to draw a widget. */
      loading = null
      reject(new Error('The challenge could not be loaded.'))
    }
    document.head.appendChild(tag)
  })
  return loading
}

export function Challenge({
  siteKey,
  onToken,
  spentAt,
}: {
  siteKey: string | null
  /** The solved token, or null whenever it goes stale and must be redone. */
  onToken: (token: string | null) => void
  /**
   * Bumped by the form whenever it has SPENT the token it held — a send that
   * succeeded, and every send that was refused.
   *
   * A token is good once, so a refused submit leaves a widget showing a solved
   * tick beside a form that holds nothing: the person corrects what was wrong,
   * presses Send, and is told to complete a check that says it is complete.
   * Nothing they can do on the page escapes that, which is why offering the
   * check again is the form's to ask for rather than something it may assume.
   */
  spentAt?: number
}) {
  const host = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const widget = useRef<string | null>(null)
  /* The callback is read at solve time rather than closed over, so a parent
     re-rendering with a new handler does not tear down and redraw the widget —
     which would throw away a challenge the person has already solved. */
  const report = useRef(onToken)
  report.current = onToken

  useEffect(() => {
    if (!siteKey) return
    let widgetId: string | null = null
    let dropped = false

    loadScript()
      .then(() => {
        if (dropped || !host.current) return
        if (!window.turnstile) {
          /* The script resolved without defining its global — an extension, or
             a proxy answering with something else. An empty gap where the check
             belongs says nothing; the failure has to. */
          setFailed(true)
          return
        }
        setFailed(false)
        widgetId = window.turnstile.render(host.current, {
          sitekey: siteKey,
          theme: 'auto',
          /* Fills the column it was handed rather than holding a fixed 300px,
             which overflows a card at a phone's width. */
          size: 'flexible',
          callback: (token) => report.current(token),
          /* A token is good once and not for long. Both of these mean the
             person has to solve it again, and the form must stop believing it
             holds a valid one. */
          'expired-callback': () => report.current(null),
          'error-callback': () => {
            report.current(null)
            setFailed(true)
          },
        })
        widget.current = widgetId
      })
      .catch(() => {
        if (!dropped) setFailed(true)
      })

    return () => {
      dropped = true
      widget.current = null
      /* The vendor throws on an id it no longer knows, and a teardown that
         throws takes the unmount with it. */
      try {
        if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
      } catch {}
    }
  }, [siteKey])

  /* Offer the check again once the form says the token it held is spent. The
     first render holds nothing to reset, so the initial value does nothing. */
  useEffect(() => {
    if (!spentAt || !widget.current || !window.turnstile) return
    try {
      window.turnstile.reset(widget.current)
    } catch {}
    report.current(null)
  }, [spentAt])

  if (!siteKey) return null

  return (
    <div className="challenge">
      <div ref={host} className="challenge__widget" />
      {failed ? (
        <p className="challenge__failed">
          The “I am human” check could not load. Reload the page and try again,
          or reach the shul using the details on this page.
        </p>
      ) : null}
    </div>
  )
}
